import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import {
  FileText, Loader, Trash2, Upload,
} from 'lucide-react'
import {
  getGetApiCompaniesCurrentServicemodulesFilesServiceModuleIdQueryKey,
  getGetApiCompaniesCurrentServicemodulesQueryKey,
  useDeleteApiCompaniesCurrentServicemodulesFilesServiceModuleIdFile,
  useGetApiCompaniesCurrentServicemodulesFilesServiceModuleId,
  usePatchApiCompaniesCurrentServicemodulesFilesServiceModuleId,
  usePostApiCompaniesCurrentServicemodulesFilesServiceModuleIdFile,
} from '@/api/generated/company-management-user/company-management-user'
import type { FileServiceModuleModel, ServiceModuleModel } from '@/api/model'
import { Button } from '@/components/shadcn/button'
import { Checkbox } from '@/components/shadcn/checkbox'
import { Label } from '@/components/shadcn/label'
import { notify } from '@/lib/notify'
import { useStandardServiceModuleFields } from '../useStandardServiceModuleFields'
import { StandardModuleFields } from '../EditServiceModuleFields'
import { EditServiceModuleDialog } from '../EditServiceModuleDialog'

interface Props {
  module: ServiceModuleModel
  onClose: () => void
}

const MAX_FILE_SIZE = 50 * 1024 * 1024
const ACCEPT = 'image/*,application/pdf,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel'

// Only images get an inline preview; PDF/Excel show a file row instead.
function isImage(name: string | null | undefined): boolean {
  return !!name && /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(name)
}

/**
 * Editor for File modules: standard fields (column 1) + file management and
 * read-receipt toggle (column 2).
 *
 * File actions (upload/replace/delete) go through dedicated endpoints and take
 * effect immediately. The `needsReadReceipt` toggle, in contrast — like the
 * standard fields — is only persisted on save via the File endpoint
 * (full update model). The read list itself is display only and lives in the
 * open dialog.
 */
export function EditFileServiceModule({ module, onClose }: Props) {
  const { t } = useTranslation(['service-modules-file', 'service-modules', 'common'])
  const standardFields = useStandardServiceModuleFields(module)
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const { data, isLoading } = useGetApiCompaniesCurrentServicemodulesFilesServiceModuleId(module.id)
  const detail = ((data as any)?.data ?? data) as FileServiceModuleModel | undefined

  const upload = usePostApiCompaniesCurrentServicemodulesFilesServiceModuleIdFile()
  const remove = useDeleteApiCompaniesCurrentServicemodulesFilesServiceModuleIdFile()
  const patch = usePatchApiCompaniesCurrentServicemodulesFilesServiceModuleId()

  const [needsReadReceipt, setNeedsReadReceipt] = useState(false)
  const [loaded, setLoaded] = useState(false)
  useEffect(() => {
    if (detail && !loaded) {
      setNeedsReadReceipt(!!detail.needsReadReceipt)
      setLoaded(true)
    }
  }, [detail, loaded])

  const hasFile = !!detail?.downloadUrl || !!detail?.path
  const busy = upload.isPending || remove.isPending

  function invalidateFile() {
    return Promise.all([
      queryClient.invalidateQueries({
        queryKey: getGetApiCompaniesCurrentServicemodulesFilesServiceModuleIdQueryKey(module.id),
      }),
      queryClient.invalidateQueries({
        queryKey: getGetApiCompaniesCurrentServicemodulesQueryKey(),
      }),
    ])
  }

  function pickFile() {
    fileInputRef.current?.click()
  }

  async function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = '' // erlaubt erneutes Wählen derselben Datei
    if (!file) return
    if (file.size === 0) {
      notify.warning(t('editFile.fileEmpty'))
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      notify.warning(t('editFile.fileTooLarge'))
      return
    }
    try {
      await upload.mutateAsync({
        serviceModuleId: module.id,
        data: { File: file },
      })
      await invalidateFile()
      notify.success(t('editFile.toasts.uploaded'))
    } catch (error) {
      notify.apiError(error, t('editFile.toasts.uploadError'))
    }
  }

  async function handleDeleteFile() {
    try {
      await remove.mutateAsync({ serviceModuleId: module.id })
      await invalidateFile()
      notify.success(t('editFile.toasts.deleted'))
    } catch (error) {
      notify.apiError(error, t('editFile.toasts.deleteError'))
    }
  }

  async function handleSave() {
    try {
      await patch.mutateAsync({
        serviceModuleId: module.id,
        data: { ...standardFields.buildBasePayload(), needsReadReceipt },
      })
      await standardFields.saveTranslations()
      await invalidateFile()
      notify.success(t('toasts.saved', { ns: 'service-modules' }))
      onClose()
    } catch (error) {
      notify.apiError(error, t('toasts.saveError', { ns: 'service-modules' }))
    }
  }

  const specific = (
    <div className="flex flex-col gap-4">
      <section className="flex flex-col gap-3">
        <Label>{t('editFile.fileLabel')}</Label>
        {isLoading ? (
          <div className="flex items-center gap-2 py-4 text-muted-foreground">
            <Loader className="size-4 animate-spin text-primary" />
            <span className="text-sm">{t('editFile.loading')}</span>
          </div>
        ) : hasFile ? (
          <div className="flex flex-col gap-3">
            {!isImage(detail?.filename || detail?.downloadUrl || detail?.path) ? (
              <div className="flex items-center gap-3 rounded-md border p-3">
                <FileText className="size-8 text-muted-foreground shrink-0" />
                <span className="text-sm truncate flex-1">{detail?.filename || 'Dokument'}</span>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-center rounded-md border bg-muted/40 p-2">
                  <img
                    src={detail!.downloadUrl ?? detail!.path!}
                    alt={detail?.filename || 'Datei'}
                    className="max-h-48 max-w-full object-contain rounded"
                  />
                </div>
                {detail?.filename && (
                  <span className="text-xs text-muted-foreground text-center truncate">{detail.filename}</span>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={pickFile} disabled={busy}>
                <Upload className="size-4 mr-1" />
                {t('editFile.replace')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleDeleteFile} disabled={busy}>
                <Trash2 className="size-4 mr-1 text-destructive" />
                {t('buttons.delete', { ns: 'common' })}
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={pickFile}
            disabled={busy}
            className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed py-10 text-muted-foreground hover:bg-muted/50 transition-colors disabled:opacity-60"
          >
            {busy ? <Loader className="size-6 animate-spin" /> : <Upload className="size-6" />}
            <span className="text-sm">{t('editFile.uploadHint')}</span>
          </button>
        )}

        <input ref={fileInputRef} type="file" accept={ACCEPT} hidden onChange={handleFileSelected} />
      </section>

      <section className="flex items-center gap-2">
        <Checkbox
          id="needs-read-receipt"
          checked={needsReadReceipt}
          onCheckedChange={(v) => setNeedsReadReceipt(!!v)}
        />
        <label htmlFor="needs-read-receipt" className="text-sm cursor-pointer">
          {t('editFile.readReceipt')}
        </label>
      </section>
    </div>
  )

  return (
    <EditServiceModuleDialog
      id={module.id}
      dialogTitle={t('editFile.title')}
      moduleName={module.name}
      onClose={onClose}
      standard={<StandardModuleFields fields={standardFields} moduleType={module.type} />}
      specific={specific}
      onSave={handleSave}
      isSaving={patch.isPending || !loaded}
    />
  )
}
